import { SwitchThemeContext } from '@tryhackme/thm-ui-components';
/* eslint-disable no-console */
import { useContext, useEffect, useRef } from 'react';

import { displayToast } from '../helpers/display-toast';
import { useGetUserQuery } from '../slices';
import { useUpdateThemeMutation } from '../slices/theme';

export const useThemeObserver = () => {
  const { theme, setTheme } = useContext(SwitchThemeContext);
  const [updateTheme] = useUpdateThemeMutation();
  const prevThemeRef = useRef(theme);
  const prevSavedThemeRef = useRef<string | undefined>(undefined);
  const { data, isSuccess: isUserLoggedIn } = useGetUserQuery();

  const savedTheme = data?.data?.user.local?.theme;
  const isUserBanned = data?.data?.user.local?.banned;

  const canModifyTheme = isUserLoggedIn && setTheme && !isUserBanned;

  useEffect(() => {
    if (!canModifyTheme) return;
    if (!savedTheme) return;

    if (prevSavedThemeRef.current === undefined || prevSavedThemeRef.current !== savedTheme) {
      setTheme(savedTheme);
      prevSavedThemeRef.current = savedTheme;
    }
  }, [canModifyTheme, savedTheme, setTheme]);

  useEffect(() => {
    let unmounted = false;

    const update = async () => {
      if (canModifyTheme && theme !== prevThemeRef.current) {
        try {
          prevThemeRef.current = theme;
          await updateTheme(theme).unwrap();
          // eslint-disable-next-line unicorn/prefer-optional-catch-binding
        } catch (error) {
          if (unmounted) return;
          displayToast('error', 'Failed to save this theme');
        }
      }
    };

    // eslint-disable-next-line no-void
    void update();
    return () => {
      unmounted = true;
    };
  }, [canModifyTheme, theme, updateTheme]);
};
